import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Minus, Maximize2, Minimize2, GripVertical, Eye, Calendar, User, Tag } from 'lucide-react';
import { GrupoGuia } from '../types/grupos';
import { grupoGuiasService } from '../services/grupoGuias';
import { stripQuillColors } from '../lib/utils';

interface GuiaViewerPIPProps {
  guiaId: string | null;
  onClose: () => void;
}

export const GuiaViewerPIP: React.FC<GuiaViewerPIPProps> = ({ guiaId, onClose }) => {
  const [guia, setGuia] = useState<GrupoGuia | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [maximized, setMaximized] = useState(false);

  // Posição e tamanho da janela
  const [position, setPosition] = useState({ x: window.innerWidth - 560, y: 80 });
  const [size, setSize] = useState({ width: 520, height: 600 });

  // Refs para drag
  const windowRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Refs para resize
  const isResizing = useRef(false);
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0 });

  useEffect(() => {
    if (guiaId) {
      loadGuia();
    }
  }, [guiaId]);

  const loadGuia = async () => {
    if (!guiaId) return;
    try {
      setLoading(true);
      setError(null);
      const guiaData = await grupoGuiasService.buscarPorId(guiaId, true);
      setGuia(guiaData);
    } catch {
      setError('Erro ao carregar a guia');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date | string) =>
    new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });

  const parseTags = (tags: string | undefined): string[] => {
    if (!tags) return [];
    try {
      return Array.isArray(tags) ? tags : JSON.parse(tags);
    } catch {
      return [];
    }
  };

  // ── Drag handlers ──────────────────────────────────────────────────────────
  const onMouseDownDrag = useCallback((e: React.MouseEvent) => {
    if (maximized) return;
    isDragging.current = true;
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    e.preventDefault();
  }, [position, maximized]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (isDragging.current) {
        const nx = Math.max(0, Math.min(e.clientX - dragOffset.current.x, window.innerWidth - size.width));
        const ny = Math.max(0, Math.min(e.clientY - dragOffset.current.y, window.innerHeight - 60));
        setPosition({ x: nx, y: ny });
      }
      if (isResizing.current) {
        const dw = e.clientX - resizeStart.current.x;
        const dh = e.clientY - resizeStart.current.y;
        setSize({
          width: Math.max(360, resizeStart.current.width + dw),
          height: Math.max(260, resizeStart.current.height + dh),
        });
      }
    };
    const onMouseUp = () => {
      isDragging.current = false;
      isResizing.current = false;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [size]);

  // ── Resize handler ─────────────────────────────────────────────────────────
  const onMouseDownResize = useCallback((e: React.MouseEvent) => {
    if (maximized) return;
    isResizing.current = true;
    resizeStart.current = { x: e.clientX, y: e.clientY, width: size.width, height: size.height };
    e.preventDefault();
    e.stopPropagation();
  }, [size, maximized]);

  // ── Computed styles ────────────────────────────────────────────────────────
  const windowStyle: React.CSSProperties = maximized
    ? { position: 'fixed', inset: 0, width: '100vw', height: '100vh', borderRadius: 0 }
    : {
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: size.width,
        height: minimized ? 'auto' : size.height,
      };

  return (
    <div
      ref={windowRef}
      style={{ ...windowStyle, zIndex: 9999 }}
      className="bg-background border border-border shadow-2xl rounded-lg flex flex-col overflow-hidden select-none"
    >
      {/* Barra de título – área de drag */}
      <div
        onMouseDown={onMouseDownDrag}
        className="flex items-center justify-between px-3 py-2 bg-muted border-b border-border cursor-move shrink-0"
      >
        <div className="flex items-center gap-2 min-w-0">
          <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-sm font-medium text-foreground truncate">
            {loading ? 'Carregando…' : guia?.titulo || 'Guia'}
          </span>
        </div>

        <div className="flex items-center gap-1 shrink-0 ml-2">
          {/* Minimizar */}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setMinimized(v => !v)}
            className="p-1 rounded hover:bg-border transition-colors text-muted-foreground hover:text-foreground"
            title={minimized ? 'Restaurar' : 'Minimizar'}
          >
            <Minus className="h-3.5 w-3.5" />
          </button>

          {/* Maximizar / restaurar */}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => { setMaximized(v => !v); setMinimized(false); }}
            className="p-1 rounded hover:bg-border transition-colors text-muted-foreground hover:text-foreground"
            title={maximized ? 'Restaurar' : 'Maximizar'}
          >
            {maximized ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>

          {/* Fechar */}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onClose}
            className="p-1 rounded hover:bg-red-500 hover:text-white transition-colors text-muted-foreground"
            title="Fechar"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Conteúdo – oculto quando minimizado */}
      {!minimized && (
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full min-h-[200px]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[200px] gap-2 p-4">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
              <button
                onClick={loadGuia}
                className="text-blue-600 hover:underline text-sm"
              >
                Tentar novamente
              </button>
            </div>
          ) : guia ? (
            <div className="p-4">
              {/* Meta informações */}
              <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-muted-foreground border-b border-border pb-3">
                <div className="flex items-center gap-1">
                  <User className="h-3.5 w-3.5" />
                  <span>{guia.militarCriador?.nick || 'Autor desconhecido'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>{formatDate(guia.criado_em)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Eye className="h-3.5 w-3.5" />
                  <span>{guia.visualizacoes} visualizações</span>
                </div>
                {guia.categoria && (
                  <div className="flex items-center gap-1">
                    <Tag className="h-3.5 w-3.5" />
                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 rounded-full">
                      {guia.categoria}
                    </span>
                  </div>
                )}
              </div>

              {/* Descrição */}
              {guia.descricao && (
                <p className="text-sm text-foreground mb-4 leading-relaxed">{guia.descricao}</p>
              )}

              {/* Tags */}
              {parseTags(guia.tags).length > 0 && (
                <div className="mb-4 flex flex-wrap gap-1.5">
                  {parseTags(guia.tags).map((tag, i) => (
                    <span key={i} className="px-2 py-0.5 bg-muted text-muted-foreground rounded-full text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Conteúdo HTML */}
              <div className="ql-snow" style={{ border: 'none' }}>
                <div
                  className="ql-editor quill-display-content"
                  dangerouslySetInnerHTML={{ __html: stripQuillColors(guia.conteudo_html) }}
                  style={{
                    padding: 0,
                    border: 'none',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: 'var(--foreground)',
                    background: 'transparent',
                  }}
                />
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Alça de redimensionamento (canto inferior direito) */}
      {!maximized && !minimized && (
        <div
          onMouseDown={onMouseDownResize}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          style={{ background: 'transparent' }}
        >
          <svg viewBox="0 0 10 10" className="w-3 h-3 absolute bottom-0.5 right-0.5 text-muted-foreground/50">
            <path d="M8 2L2 8M5 2L2 5M8 5L5 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
      )}
    </div>
  );
};
